import { Injectable } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { AppConfigurationService } from '../app-configuration.service'
import { catchError, map, retry } from 'rxjs/operators'
import { Observable, of } from 'rxjs'
import { UrlSegment } from '@angular/router'
import { Roles, UserGroups } from '../../models/enum/roles-permissions.enum'
import { CustomRoutes } from '../../models/enum/custom-routes.enum'

@Injectable({
  providedIn: 'root'
})
export class UserPermissionService {
  private userRoles: Array<{ name: string; groups: string[] }> = [
    {
      name: Roles.ADMIN,
      groups: UserGroups.ADMIN
    },
    {
      name: Roles.NO_PRICING,
      groups: UserGroups.NO_PRICING
    },
    {
      name: Roles.NO_PRICING_NO_ORDERS,
      groups: UserGroups.NO_PRICING_NO_ORDERS
    },
    {
      name: Roles.NO_ACCOUNTS,
      groups: UserGroups.NO_ACCOUNTS
    }
  ]

  public showPrice: boolean
  public addToCart: boolean
  public invoicesAndCreditNotes: boolean
  public downloadProductInfo: boolean

  constructor(
    public http: HttpClient,
    public appConfigurationService: AppConfigurationService
  ) {}

  public canVisit(urls: UrlSegment[]): Observable<boolean> {
    const requestedUrl = this.getRequestedUrl(urls)
    return this.getUserGroups().pipe(
      map((userGroups) => {
        const userRole = this.getUserRoleFromUserGroups(userGroups)
        return this.canUserVisit(requestedUrl, userRole)
      })
    )
  }

  public canVisitWithoutLogin(urls: UrlSegment[]): Observable<boolean> {
    const requestedUrl = this.getRequestedUrl(urls)
    return of(this.canUserVisit(requestedUrl))
  }

  private getRequestedUrl(urls: UrlSegment[]): string {
    for (const url of urls) {
      if (url.path !== 'my-account') {
        return url.path
      }
    }
    return null
  }

  private getUserGroups(): Observable<any> {
    const baseSiteUrl = this.appConfigurationService.getBaseSiteUrl()
    return this.http.get<any>(`${baseSiteUrl}/orgUsers/current`).pipe(
      map((res) => {
        sessionStorage.setItem('userDetails', JSON.stringify(res))
        return res?.roles
      }),
      retry(1),
      catchError(() => {
        return of(null)
      })
    )
  }

  private getUserRoleFromUserGroups(userGroups: string[]): string {
    for (const itemRole of this.userRoles) {
      let groupCount = 0
      for (const itemGroup of itemRole.groups) {
        const groupFound = !!userGroups?.find((userGroup) => itemGroup === userGroup)
        if (groupFound) {
          groupCount += 1
        } else {
          break
        }
      }
      if (groupCount === itemRole.groups.length) {
        return itemRole.name
      }
    }
    return null
  }

  private canUserVisit(requestedUrl: string, userRole?: string): boolean {
    switch (userRole) {
      case Roles.ADMIN:
        return this.canAdminVisit()

      case Roles.NO_ACCOUNTS:
        return this.canNoAccountsVisit(requestedUrl)

      case Roles.NO_PRICING:
        return this.canNoPricingVisit(requestedUrl)

      case Roles.NO_PRICING_NO_ORDERS:
        return this.canNoPricingNoOrdersVisit(requestedUrl)

      default:
        return this.canGuestVisit(requestedUrl)
    }
  }

  private canAdminVisit(): boolean {
    this.applyAdminRoles()
    return true
  }
  private applyAdminRoles(): void {
    this.showPrice = true
    this.addToCart = true
    this.invoicesAndCreditNotes = true
    this.downloadProductInfo = true
  }

  private canNoAccountsVisit(requestedUrl: string): boolean {
    this.applyNoAccountsRoles()
    switch (requestedUrl) {
      case CustomRoutes.ORDER_HISTORY:
      case CustomRoutes.ORDER_DETAIL:
      case CustomRoutes.IMPORT_SAVED_CART:
      case CustomRoutes.SAVED_CARTS:
      case CustomRoutes.CART:
      case CustomRoutes.CHECKOUT:
      case CustomRoutes.ORDER_CONFIRMATION:
        return false
      default:
        return true
    }
  }
  private applyNoAccountsRoles(): void {
    this.showPrice = false
    this.addToCart = false
    this.invoicesAndCreditNotes = false
    this.downloadProductInfo = true
  }

  private canNoPricingVisit(requestedUrl: string): boolean {
    this.applyNoPricingRoles()
    switch (requestedUrl) {
      case CustomRoutes.PRICE_SUPPORT_AGREEMENT_HISTORY:
      case CustomRoutes.PRICE_SUPPORT_AGREEMENT_DETAILS:
      case CustomRoutes.REBATE_CLAIMS:
      case CustomRoutes.REBATE_CLAIM_DETAILS:
      case CustomRoutes.CREATE_REBATE_CLAIM:
      case CustomRoutes.SHIPMENT_CREDIT_CLAIM:
      case CustomRoutes.SHIPMENT_CREDIT_CLAIM_HISTORY:
      case CustomRoutes.SHIPMENT_CREDIT_CLAIM_DETAIL:
        return false
      default:
        return true
    }
  }
  private applyNoPricingRoles(): void {
    this.showPrice = true
    this.addToCart = true
    this.invoicesAndCreditNotes = false
    this.downloadProductInfo = true
  }

  private canNoPricingNoOrdersVisit(requestedUrl: string): boolean {
    this.applyNoPricingNoOrdersRoles()
    switch (requestedUrl) {
      case CustomRoutes.PRICE_SUPPORT_AGREEMENT_HISTORY:
      case CustomRoutes.PRICE_SUPPORT_AGREEMENT_DETAILS:
      case CustomRoutes.ORDER_HISTORY:
      case CustomRoutes.ORDER_DETAIL:
      case CustomRoutes.IMPORT_SAVED_CART:
      case CustomRoutes.SAVED_CARTS:
      case CustomRoutes.CART:
      case CustomRoutes.CHECKOUT:
      case CustomRoutes.ORDER_CONFIRMATION:
      case CustomRoutes.REBATE_CLAIMS:
      case CustomRoutes.REBATE_CLAIM_DETAILS:
      case CustomRoutes.CREATE_REBATE_CLAIM:
      case CustomRoutes.SHIPMENT_CREDIT_CLAIM:
      case CustomRoutes.SHIPMENT_CREDIT_CLAIM_HISTORY:
      case CustomRoutes.SHIPMENT_CREDIT_CLAIM_DETAIL:
        return false
      default:
        return true
    }
  }
  private applyNoPricingNoOrdersRoles(): void {
    this.showPrice = false
    this.addToCart = false
    this.invoicesAndCreditNotes = false
    this.downloadProductInfo = true
  }

  private canGuestVisit(requestedUrl: string): boolean {
    this.applyGuestRoles()
    switch (requestedUrl) {
      case CustomRoutes.PRICE_SUPPORT_AGREEMENT_HISTORY:
      case CustomRoutes.PRICE_SUPPORT_AGREEMENT_DETAILS:
      case CustomRoutes.ORDER_HISTORY:
      case CustomRoutes.ORDER_DETAIL:
      case CustomRoutes.IMPORT_SAVED_CART:
      case CustomRoutes.SAVED_CARTS:
      case CustomRoutes.CART:
      case CustomRoutes.CHECKOUT:
      case CustomRoutes.ORDER_CONFIRMATION:
      case CustomRoutes.DISPENSER_AGREEMENT_HISTORY:
      case CustomRoutes.DISPENSER_AGREEMENT_DETAILS:
      case CustomRoutes.DISPENSER_AGREEMENT_SHIPMENT_HISTORY:
      case CustomRoutes.DISPENSER_AGREEMENT_SHIPMENT_DETAILS:
      case CustomRoutes.DISPENSER_AGREEMENT_PLACE_ORDER:
      case CustomRoutes.CREATE_DISPENSER_AGREEMENT:
      case CustomRoutes.NEW_DISPENSER_CREDIT_REQUEST:
      case CustomRoutes.REBATE_CLAIMS:
      case CustomRoutes.REBATE_CLAIM_DETAILS:
      case CustomRoutes.CREATE_REBATE_CLAIM:
      case CustomRoutes.CREDIT_CLAIMS:
      case CustomRoutes.CREDIT_CLAIM_DETAILS:
      case CustomRoutes.CREATE_CREDIT_CLAIM_ORDER:
      case CustomRoutes.ACCOUNT_SUMMARY:
      case CustomRoutes.SHIPMENT_CREDIT_CLAIM:
      case CustomRoutes.SHIPMENT_CREDIT_CLAIM_HISTORY:
      case CustomRoutes.SHIPMENT_CREDIT_CLAIM_DETAIL:
        return false
      default:
        return true
    }
  }
  public applyGuestRoles(): void {
    this.showPrice = false
    this.addToCart = false
    this.invoicesAndCreditNotes = false
    this.downloadProductInfo = false
  }

  public getUserMinimumSaleOrderAmount(): Observable<any> {
    const baseSiteUrl = this.appConfigurationService.getBaseSiteUrl()
    return this.http.get<any>(`${baseSiteUrl}/users/current/getMinimumSaleOrderAmount`).pipe(
      map((res) => {
        return res
      }),
      retry(1),
      catchError(() => {
        return of(null)
      })
    )
  }
}
